/**
 * Esteira de cross-sell — seleção do slide (06/08/2026).
 *
 * O Liquid emite TODOS os candidatos como slides, em ordem de prioridade
 * (gatilho primeiro, depois a coleção `cs-esteira` na ordem de mais vendidos).
 * Quem escolhe qual aparece é este módulo, porque a regra "nunca repetir o que já
 * foi mostrado" é estado de sessão e o Liquid é stateless.
 *
 * Duas chaves, e a distinção entre elas é o coração da feature:
 *   SHOWN   — handles QUEIMADOS, que nunca mais voltam.
 *   CURRENT — o handle exibido agora, que ainda NÃO está queimado.
 *
 * Queima só em dois eventos: (a) o cliente aceitou a oferta, (b) a oferta foi
 * DESLOCADA por outra de prioridade maior. Um mesmo card sobrevivendo a vários
 * re-renders do drawer (mudança de quantidade, frete, cupom) não é repetição —
 * queimar ali comeria a fila inteira em três cliques.
 */
const CS_KEY_SHOWN = 'hoomy_cs_shown';
const CS_KEY_CURRENT = 'hoomy_cs_current';

/*
 * A "data de hoje" no titulo do cross-sell (24/08/2026) NAO foi portada pro perpetuo:
 * Thiago, 31/08 — "vamos manter o design mas a data do dia eu acho desnecessario".
 * O titulo passa a ser o texto puro de `data-cs-headline` / `cs_headline_unica`.
 */
/** @returns {string[]} */
function csReadShown() {
  try {
    const raw = sessionStorage.getItem(CS_KEY_SHOWN);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

/** @param {string[]} list */
function csWriteShown(list) {
  try {
    sessionStorage.setItem(CS_KEY_SHOWN, JSON.stringify(list));
  } catch (error) {
    // sessionStorage indisponível (modo privado antigo): a esteira degrada pra
    // "sempre o primeiro candidato", que é o comportamento de hoje.
  }
}

/** @param {string} handle */
function csBurn(handle) {
  if (!handle) return;
  const shown = csReadShown();
  if (!shown.includes(handle)) {
    shown.push(handle);
    csWriteShown(shown);
  }
}

function csCurrent() {
  try {
    return sessionStorage.getItem(CS_KEY_CURRENT) || '';
  } catch (error) {
    return '';
  }
}

/** @param {string} handle */
function csSetCurrent(handle) {
  try {
    if (handle) sessionStorage.setItem(CS_KEY_CURRENT, handle);
    else sessionStorage.removeItem(CS_KEY_CURRENT);
  } catch (error) {
    // ver csWriteShown
  }
}

/**
 * Trackeamento de IMPRESSÃO (11/08/2026) — evento `order_bump_impression`.
 *
 * Dispara quando o slide escolhido fica de fato VISÍVEL (drawer aberto), uma vez
 * por IDENTIDADE por sessão — abrir e fechar o drawer N vezes é 1 impressão, não N.
 * O IntersectionObserver decide o "quando"; a dedupe mora no sessionStorage porque
 * o drawer recria este elemento a cada mudança de carrinho e cada render nasce com
 * um observer novo — sem a dedupe persistida, cada re-render contaria de novo.
 *
 * `ob_logic` vem do container (`data-ob-logic`, hoje "estatico"): é o campo que
 * separa a lógica vigente de uma futura variante em teste. Par do evento de
 * aceite `order_bump_add_to_cart` (cart-orderbump-product.js), mesmo `ob_data`.
 */
const OB_IMP_KEY = 'hoomy_ob_impressions';

/** @returns {string[]} */
function obReadImpressions() {
  try {
    const raw = sessionStorage.getItem(OB_IMP_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

/** @param {string} identity */
function obMarkImpression(identity) {
  try {
    const seen = obReadImpressions();
    if (!seen.includes(identity)) {
      seen.push(identity);
      sessionStorage.setItem(OB_IMP_KEY, JSON.stringify(seen));
    }
  } catch (error) {
    // sessionStorage indisponível: sem dedupe o evento pode repetir — melhor
    // superestimar num navegador raro do que perder o evento em todos.
  }
}

/**
 * O IO cruza por GEOMETRIA, não por visibilidade computada: drawer escondido via
 * `visibility: hidden` ainda "intersecta" e contaria impressão de drawer fechado.
 * Recheque nos moldes do #isVisible do componente antes de contar.
 * @param {HTMLElement} el
 */
function obElementVisible(el) {
  const rect = el.getBoundingClientRect();
  const style = getComputedStyle(el);
  return rect.height > 0 && rect.width > 0 && style.visibility !== 'hidden' && style.display !== 'none';
}

/** @param {HTMLElement} slide */
function obTrackImpression(slide) {
  const identity = slide.getAttribute('data-cs-identity') || slide.getAttribute('data-cs-handle') || '';
  if (!identity) return;
  if (obReadImpressions().includes(identity)) return;
  obMarkImpression(identity);

  const container = slide.closest('#cart-order-bump');
  const productEl = slide.querySelector('cart-order-bump-product');
  const w = /** @type {any} */ (window);
  w.dataLayer = w.dataLayer || [];
  // Zera o objeto antes de reusar a chave: no modelo do data layer, valor de push
  // persiste e vazaria pra leitura de outras tags (convenção `ecommerce: null` do tema).
  w.dataLayer.push({ ob_data: null });
  w.dataLayer.push({
    event: 'order_bump_impression',
    ob_data: {
      ob_logic: (container && container.getAttribute('data-ob-logic')) || 'estatico',
      ob_layer: productEl && productEl.hasAttribute('data-lazy-product') ? 'esteira' : 'gatilho',
      ob_handle: slide.getAttribute('data-cs-handle') || '',
      ob_identity: identity,
      ob_variant_id: (productEl && productEl.getAttribute('data-default-variant-id')) || ''
    }
  });
}

/**
 * Observa o(s) slide(s) não ocultos deste render. Com esteira ligada o csPickSlide
 * deixa exatamente um; com esteira desligada o slide de gatilho já nasce visível
 * do Liquid — os dois modos passam por aqui. O unobserve é só limpeza: quem
 * garante "1 por sessão" é a dedupe.
 * @param {ParentNode} root
 */
function obObserveImpression(root) {
  if (typeof IntersectionObserver === 'undefined') return;
  const slides = Array.from(root.querySelectorAll('.cart-order-bump__item')).filter(
    (slide) => slide instanceof HTMLElement && !slide.hidden
  );
  if (slides.length === 0) return;
  const observer = new IntersectionObserver(
    (entries, io) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        if (!(el instanceof HTMLElement) || !obElementVisible(el)) return;
        obTrackImpression(el);
        io.unobserve(el);
      });
    },
    { threshold: 0.5 }
  );
  slides.forEach((slide) => observer.observe(slide));
}

/**
 * Escolhe o único slide que fica no DOM. Idempotente: rodar de novo sobre o
 * resultado não queima nada (sobrou 1 slide, que já é o `current`).
 * @param {ParentNode} root
 */
function csPickSlide(root) {
  // Sem o marcador do Liquid a esteira está desligada (flag off ou coleção vazia):
  // sair aqui é o que mantém o bloco byte-a-byte no comportamento pré-esteira —
  // nada de `current`, nada de queima, nenhuma oferta filtrada.
  if (!root.closest('#cart-order-bump[data-cs-esteira="on"]')) return;

  const slides = Array.from(root.querySelectorAll('[data-cs-handle]'));
  if (slides.length === 0) return;

  // Chave = IDENTIDADE, não handle: com o catálogo inteiro na esteira o mesmo
  // produto aparece sob handles diferentes (versões de LP, sósia de campanha) e
  // mostrar de novo seria repetir a oferta com outra roupa.
  const shown = csReadShown();

  // Teto de ofertas por SESSÃO (regra do Thiago, 07/08): a do fluxo de gatilho
  // + no máximo mais 2 depois dela. `shown` guarda as já queimadas; quando ele
  // atinge o teto, a fila acabou — mesmo que ainda existam candidatos novos.
  // A oferta em exibição (current) não conta até ser aceita/deslocada: o teto
  // limita PROGRESSÃO, não presença.
  const container = root.closest('#cart-order-bump');
  const maxOffers = parseInt(container.getAttribute('data-cs-max') || '3', 10) || 3;
  if (shown.length >= maxOffers) {
    slides.forEach((slide) => slide.remove());
    const cappedHeader = document.querySelector('.cart-order-bump__header');
    if (cappedHeader instanceof HTMLElement) cappedHeader.remove();
    csSetCurrent('');
    return;
  }
  const candidates = slides.filter((slide) => {
    const id = slide.getAttribute('data-cs-identity') || slide.getAttribute('data-cs-handle');
    return id ? !shown.includes(id) : false;
  });

  // Fila esgotada: nenhum candidato novo, some com os cards E com o cabeçalho —
  // senão sobra um título anunciando uma oferta que não existe mais.
  if (candidates.length === 0) {
    slides.forEach((slide) => slide.remove());
    const orphanHeader = document.querySelector('.cart-order-bump__header');
    if (orphanHeader instanceof HTMLElement) orphanHeader.remove();
    csSetCurrent('');
    return;
  }

  const selected = candidates[0];
  const selectedHandle =
    selected.getAttribute('data-cs-identity') || selected.getAttribute('data-cs-handle') || '';
  const previous = csCurrent();

  // Deslocada por uma oferta de prioridade maior: a anterior não volta.
  if (previous && previous !== selectedHandle) {
    csBurn(previous);
  }
  csSetCurrent(selectedHandle);

  slides.forEach((slide) => {
    if (slide !== selected) slide.remove();
  });
  selected.removeAttribute('hidden');

  // O cabeçalho pertence à oferta, não ao bloco — acompanha o slide escolhido.
  const headline = selected.getAttribute('data-cs-headline');
  const title = document.querySelector('[data-cs-header-title]');
  if (title instanceof HTMLElement && headline) {
    title.textContent = headline;
  }
}

// Aceitou a oferta: só o slide visível pode ser adicionado, então queima o current.
document.addEventListener('orderBumpAdd', () => {
  const current = csCurrent();
  if (current) {
    csBurn(current);
    csSetCurrent('');
  }
});

/**
 * Cart Order Bump Component
 * Manages a Flickity carousel for recommended products in the cart drawer
 */
class cartOrderBump extends HTMLElement {
  /** @type {Object|null} */
  #flickityInstance = null;

  /** @type {Object} */
  static #FLICKITY_CONFIG = {
    cellAlign: 'left',
    contain: true,
    pageDots: false,
    prevNextButtons: true,
    wrapAround: false
  };

  /** @type {string} */
  static #CAROUSEL_SELECTOR = '.main-carousel';
  
  /** @type {string} */
  static #CONTAINER_SELECTOR = '#cart-order-bump';
  
  /** @type {string} */
  static #ITEM_SELECTOR = '.cart-order-bump__item';

  constructor() {
    super();
    this.#setupEventListeners();
  }

  connectedCallback() {
    // A escolha do slide não pode depender de o drawer estar ABERTO: medido em
    // 06/08 neste tema, `dialog:open` nunca é disparado no document — o listener
    // abaixo é código morto e o que sobrava era o polling de 2s, que expira 10s
    // depois de o elemento nascer. Como o drawer recria este elemento a cada
    // render, connectedCallback é o gancho confiável.
    //
    // Chamada dupla de propósito: custom element definido antes do parse recebe
    // connectedCallback com a tag de abertura, ainda sem os filhos. A 1ª chamada
    // atende o caso em que os slides já existem, a 2ª (fim do task) o caso normal.
    // csPickSlide é idempotente e sai cedo quando não há slide.
    csPickSlide(this);
    setTimeout(() => {
      csPickSlide(this);
        // Depois da escolha: sobrou o slide definitivo deste render, é ele que
      // conta impressão quando ficar visível (drawer aberto de verdade).
      obObserveImpression(this);
    }, 0);

    if (this.#isVisible()) {
      setTimeout(() => this.initCarousel(), 100);
    }
  }

  /**
   * Sets up event listeners for cart drawer opening
   */
  #setupEventListeners() {
    // Primary approach: Listen for dialog open events
    document.addEventListener('dialog:open', (event) => {
      // Check if the opened dialog contains our cart-order-bump element
      if (event.target instanceof HTMLElement) {
        const hasOrderBump = event.target.querySelector('cart-order-bump') !== null;
        
        if (hasOrderBump) {
          setTimeout(() => this.initCarousel(), 150);
        }
      }
    });

    // Fallback approach: Listen for cart updates (when drawer might already be open)
    document.addEventListener('cart:update', () => {
      // Small delay to allow DOM to update
      setTimeout(() => {
        if (this.#isVisible()) {
          this.initCarousel();
        }
      }, 100);
    });

    // Additional fallback: Check periodically if element becomes visible
    const checkVisibility = () => {
      const carouselElement = document.querySelector(cartOrderBump.#CAROUSEL_SELECTOR);
      if (this.#isVisible() && carouselElement && !this.#isFlickityWorking(carouselElement)) {
        this.initCarousel();
      }
    };

    // Check every 2 seconds for the first 10 seconds after element creation
    let checkCount = 0;
    const intervalId = setInterval(() => {
      checkVisibility();
      checkCount++;
      if (checkCount >= 5) { // Stop after 10 seconds
        clearInterval(intervalId);
      }
    }, 2000);
  }

  /**
   * Initializes or reinitializes the Flickity carousel
   * Called automatically when cart drawer opens or by connectedCallback when already visible
   */
  initCarousel() {
    // Roda ANTES do Flickity: ele precisa enxergar uma célula só.
    csPickSlide(this);

    const carouselElement = this.#getCarouselElement();
    if (!carouselElement) {
      return;
    }

    const items = carouselElement.querySelectorAll(cartOrderBump.#ITEM_SELECTOR);
    if (items.length === 0) return;

    // 11/08: com a esteira ligada, o csPickSlide acima deixa EXATAMENTE um slide no DOM,
    // e as setas já ficavam escondidas por `[data-item-count="1"]`. Rodar o Flickity num
    // carrossel de uma célula só não entregava nada e trazia não-determinismo: ele
    // inicializa por corrida (connectedCallback quando o drawer já está aberto, senão o
    // polling que expira 10s depois do elemento nascer — o `dialog:open` nunca dispara
    // neste tema, como o próprio comentário do connectedCallback registra). Como
    // `.flickity-enabled` muda flex-direction, overflow e margin-bottom, o mesmo carrinho
    // rendia dois layouts diferentes dependendo de quando o cliente abria o drawer.
    const visibleItems = Array.from(items).filter(
      (item) => !(item instanceof HTMLElement && item.hidden)
    );
    if (visibleItems.length <= 1) {
      const singleContainer = document.querySelector(cartOrderBump.#CONTAINER_SELECTOR);
      if (singleContainer) singleContainer.setAttribute('data-item-count', String(visibleItems.length));
      if (carouselElement.classList.contains('flickity-enabled')) {
        this.#destroyExistingFlickity(carouselElement);
      }
      return;
    }

    if (this.#shouldRecreateFlickity(carouselElement)) {
      this.#destroyExistingFlickity(carouselElement);
    } else if (this.#isFlickityWorking(carouselElement)) {
      return; // Already working properly
    }

    if (this.#isVisible()) {
      this.#createFlickityInstance(carouselElement);
    }
  }

  /**
   * Gets the carousel DOM element
   * @returns {Element|null}
   */
  #getCarouselElement() {
    const element = document.querySelector(cartOrderBump.#CAROUSEL_SELECTOR);
    return element?.parentNode ? element : null;
  }

  /**
   * Checks if the order bump container is visible in the viewport
   * @returns {boolean}
   */
  #isVisible() {
    const container = document.querySelector(cartOrderBump.#CONTAINER_SELECTOR);
    if (!container) return false;

    const rect = container.getBoundingClientRect();
    const style = getComputedStyle(container);
    
    return (
      rect.height > 0 &&
      rect.width > 0 &&
      style.visibility !== 'hidden' &&
      style.display !== 'none' &&
      style.opacity !== '0'
    );
  }

  /**
   * Checks if Flickity is already working properly
   * @param {Element} element 
   * @returns {boolean}
   */
  #isFlickityWorking(element) {
    if (!element.classList.contains('flickity-enabled')) return false;
    
    const viewport = element.querySelector('.flickity-viewport');
    return viewport instanceof HTMLElement && viewport.offsetHeight > 0;
  }

  /**
   * Determines if Flickity needs to be recreated
   * @param {Element} element 
   * @returns {boolean}
   */
  #shouldRecreateFlickity(element) {
    return element.classList.contains('flickity-enabled') && !this.#isFlickityWorking(element);
  }

  /**
   * Safely destroys existing Flickity instance
   * @param {Element} element 
   */
  #destroyExistingFlickity(element) {
    try {
      // @ts-ignore - Flickity is loaded globally
      const existingInstance = Flickity.data(element);
      if (existingInstance) {
        existingInstance.destroy();
      }
    } catch (error) {
      // Fallback: manually remove classes if destroy fails
      element.classList.remove('flickity-enabled', 'flickity-rtl');
    }
  }

  /**
   * Creates a new Flickity instance
   * @param {Element} element 
   */
  #createFlickityInstance(element) {
    try {
      const items = element.querySelectorAll(cartOrderBump.#ITEM_SELECTOR);
      
      // Add data attribute for CSS styling
      const container = document.querySelector(cartOrderBump.#CONTAINER_SELECTOR);
      if (container) {
        container.setAttribute('data-item-count', items.length.toString());
      }
      
      // Create config with conditional arrows
      const config = {
        cellAlign: 'left',
        contain: true,
        pageDots: false,
        prevNextButtons: items.length > 1, // Hide arrows when there's only 1 item
        wrapAround: false
      };
      
      // @ts-ignore - Flickity is loaded globally
      this.#flickityInstance = new Flickity(element, config);
    } catch (error) {
      // Silently fail - Flickity may not be loaded yet
    }
  }
}

customElements.define('cart-order-bump', cartOrderBump);